/* App-styled single-select dropdown.
 *
 * Vanilla port of adj.news src/components/ui/Select.tsx + Select.css, in the
 * boxed variant the markets filter bar uses. Native <select> renders the OS
 * menu, which ignores our chrome entirely.
 *
 * Progressive enhancement: the real <select> stays in the DOM (hidden) and
 * remains the source of truth. Selecting an option writes back to it and fires
 * a bubbling `change`, so existing listeners and the disabled/locked handling
 * keep working untouched.
 *
 * Keyboard follows the WAI-ARIA collapsed listbox pattern: Enter/Space/Arrows
 * open; while open Arrows move the active option (wrapping), Home/End jump,
 * Enter/Space select and close, Escape closes without changing, Tab closes,
 * and typing does type-ahead.
 */

const TYPEAHEAD_RESET_MS = 500;

let uid = 0;

function enhanceSelect(select) {
  if (select.dataset.uiSelect === 'on') return;
  select.dataset.uiSelect = 'on';

  const id = `ui-select-${++uid}`;
  const root = document.createElement('div');
  root.className = 'ui-select';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'ui-select-trigger';
  trigger.id = `${id}-trigger`;
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  const label = select.getAttribute('aria-label');
  if (label) trigger.setAttribute('aria-label', label);

  const value = document.createElement('span');
  value.className = 'ui-select-value';
  const chevron = document.createElement('span');
  chevron.className = 'ui-select-chevron';
  chevron.setAttribute('aria-hidden', 'true');
  chevron.textContent = '▾';
  trigger.append(value, chevron);

  const menu = document.createElement('ul');
  menu.className = 'ui-select-menu';
  menu.setAttribute('role', 'listbox');
  menu.tabIndex = -1;
  // Top layer, so the menu never has to out-rank the table on z-index and can
  // never be clipped by an ancestor. "manual" keeps dismissal ours: "auto"
  // light-dismisses on the trigger's own pointerdown and re-opens on its click.
  menu.popover = 'manual';
  if (label) menu.setAttribute('aria-label', label);

  select.parentNode.insertBefore(root, select);
  root.append(trigger, menu, select);
  root.parentElement
    ?.querySelector('.db-field-caption')
    ?.addEventListener('click', () => trigger.click());

  // `open` is derived, not stored: the popover's own state is the truth.
  const isOpen = () => menu.matches(':popover-open');
  let activeIndex = -1;
  const typeahead = { buffer: '', timer: null };

  const paintTrigger = () => {
    const selected = select.selectedOptions[0] ?? select.options[0];
    value.textContent = selected?.textContent ?? '';
    trigger.disabled = select.disabled;
    trigger.title = select.title || '';
  };

  const paintMenu = () => {
    menu.innerHTML = '';
    [...select.options].forEach((opt, i) => {
      const li = document.createElement('li');
      li.className = 'ui-select-option';
      li.id = `${id}-option-${i}`;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', String(i === select.selectedIndex));
      li.classList.toggle('is-active', i === activeIndex);
      li.textContent = opt.textContent;
      li.addEventListener('mouseenter', () => setActive(i));
      li.addEventListener('click', () => choose(i));
      menu.append(li);
    });
    menu.setAttribute('aria-activedescendant', activeIndex >= 0 ? `${id}-option-${activeIndex}` : '');
  };

  const setActive = (i) => {
    activeIndex = i;
    for (const [n, li] of [...menu.children].entries()) li.classList.toggle('is-active', n === i);
    menu.setAttribute('aria-activedescendant', i >= 0 ? `${id}-option-${i}` : '');
    menu.children[i]?.scrollIntoView({ block: 'nearest' });
  };

  const openMenu = () => {
    if (isOpen() || select.disabled) return;
    activeIndex = Math.max(0, select.selectedIndex);
    paintMenu();
    menu.showPopover();
    // Top-layer elements position against the viewport, so anchor by hand.
    const r = trigger.getBoundingClientRect();
    menu.style.top = `${r.bottom + 4}px`;
    menu.style.left = `${r.left}px`;
    menu.style.minWidth = `${r.width}px`;
    trigger.setAttribute('aria-expanded', 'true');
    menu.focus();
  };

  const closeMenu = (returnFocus = true) => {
    if (!isOpen()) return;
    menu.hidePopover();
    trigger.setAttribute('aria-expanded', 'false');
    if (returnFocus) trigger.focus();
  };

  const choose = (i) => {
    // Close before dispatching: the `change` listener re-renders the table and
    // can flip the select's disabled state, and anything it does must not be
    // able to leave the menu stuck open.
    closeMenu();
    if (!select.options[i]) return;
    select.selectedIndex = i;
    // The `change` listener below repaints the trigger.
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const step = (from, dir) => {
    const n = select.options.length;
    if (!n) return -1;
    return (((from + dir) % n) + n) % n;
  };

  trigger.addEventListener('click', () => (isOpen() ? closeMenu() : openMenu()));

  trigger.addEventListener('keydown', (e) => {
    if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      openMenu();
    }
  });

  menu.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.preventDefault(); closeMenu(); return; }
    if (e.key === 'Tab') { closeMenu(false); return; }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(activeIndex); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(step(activeIndex, 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive(step(activeIndex, -1)); return; }
    if (e.key === 'Home') { e.preventDefault(); setActive(0); return; }
    if (e.key === 'End') { e.preventDefault(); setActive(select.options.length - 1); return; }
    if (e.key.length !== 1) return;
    // Type-ahead: jump to the first option starting with the buffer.
    typeahead.buffer += e.key.toLowerCase();
    clearTimeout(typeahead.timer);
    typeahead.timer = setTimeout(() => { typeahead.buffer = ''; }, TYPEAHEAD_RESET_MS);
    const hit = [...select.options].findIndex((o) =>
      o.textContent.toLowerCase().startsWith(typeahead.buffer),
    );
    if (hit >= 0) setActive(hit);
  });

  document.addEventListener('mousedown', (e) => {
    if (isOpen() && !root.contains(e.target)) closeMenu(false);
  });

  // The filter <select>s are repopulated after the record loads, and are
  // disabled for guests — mirror both.
  new MutationObserver(() => {
    paintTrigger();
    if (isOpen()) paintMenu();
  }).observe(select, { childList: true, attributes: true, attributeFilter: ['disabled', 'title'] });

  select.addEventListener('change', paintTrigger);

  paintTrigger();
}

for (const el of document.querySelectorAll('.db-field select')) enhanceSelect(el);
